/**
 * StageSim — deterministic Donkey Kong-style stage for "Dams to AGI".
 *
 * Run and jump on girders, climb ladders, chuck wood (alternate W/C) to build
 * broken ladders, dodge Kong-Bot's barrels, pry energy leeches off turbines.
 * Energy (integer Wh) is the score; wood logs = Wh / whPerLog.
 *
 * Pure and fixed-step (50Hz, integer sub-pixels): the same seed and input log
 * [[t, key]] always replay to the same result. Keys: held L± R± U± D±, taps J W C.
 * Within a tick: player → barrels → sparks → collisions → robots/energy →
 * spawns → end check (part of the replay contract).
 */

import ComboSystem from "../../game/ComboSystem";
import { isCorrectTap } from "../../game/GameRules";
import { rngFrom, nextInt, deriveSeed } from "../rng";
import { getStageConfig } from "./stageLevels";
import {
  TICK_MS,
  WALK_SUB,
  CLIMB_SUB,
  JUMP_TICKS,
  jumpHeightSub,
  toSub,
  aligned,
} from "./physics";

export const STAGE_SIM_VERSION = 1;

const TICKS_PER_SEC = 1000 / TICK_MS;
const JUMP_BUFFER_TICKS = 5; // 100ms
const MIN_X = toSub(8);
const MAX_X = toSub(216);
const BARREL_FALL_TICKS = 8;
const BARREL_LADDER_SUB = 16;
const HIT_DX = toSub(10);
const HIT_DY = toSub(12);
const CLEAR_HEIGHT = toSub(6); // jump height that clears a barrel
const BITE_REACH = toSub(14);
const PICKUP_DX = toSub(8);
const WORK_DX_PX = 12;
const COMBO_CLOCK_OFFSET = 1; // ComboSystem treats time 0 as "never tapped"

const HELD = { "L+": ["L", true], "L-": ["L", false], "R+": ["R", true], "R-": ["R", false], "U+": ["U", true], "U-": ["U", false], "D+": ["D", true], "D-": ["D", false] };

/** Barrels roll right on odd tiers and left on even tiers (tier 5 = top). */
const tierDir = (tier) => (tier % 2 === 1 ? 1 : -1);

// ── create ──────────────────────────────────────────────────────────

/**
 * @param {{ level: number, seed: number, startBankWh?: number, startLives?: number, overrides?: object }} p
 *   startLives: lives carried in from earlier stages (arcade credits); defaults to the stage's.
 */
export function createStageSim({ level, seed, startBankWh, startLives, overrides = {} }) {
  const config = { ...getStageConfig(level), ...overrides };
  const tierY = (tier) => toSub(config.tiersY[tier]);
  return {
    level,
    kind: config.kind,
    config,
    status: "playing",
    tick: 0,
    t: 0,
    endedAt: null,
    keys: { L: false, R: false, U: false, D: false },
    jumpRequestUntil: -1,
    player: {
      x: toSub(config.start.x),
      y: tierY(config.start.tier),
      tier: config.start.tier,
      mode: "ground",
      vx: 0,
      jumpTick: 0,
      ladderId: null,
      biteUntil: 0,
      stunUntil: 0,
      invulnUntil: 0,
      entryX: toSub(config.start.x),
    },
    ladders: config.ladders.map((l) => ({ ...l, x: toSub(l.x) })),
    turbines: config.turbines.map((tb) => ({ ...tb, x: toSub(tb.x) })),
    leeches: [],
    leechQueue: config.turbines.map((tb, i) => ({ turbine: tb.id, at: 50 + 100 * i })),
    barrels: [],
    barrelSeq: 0,
    nextBarrelTick: config.barrelEveryTicks,
    sparks: config.sparks.map((sp, i) => ({ id: i, x: toSub(sp.x), y: tierY(sp.tier), tier: sp.tier, mode: "ground", ladderId: null, dir: sp.x > config.start.x ? -1 : 1 })),
    bite: { ...config.bite, x: toSub(config.bite.x), taken: false },
    nextCrackTick: config.crackEveryTicks || Number.MAX_SAFE_INTEGER,
    lives: startLives ?? config.lives,
    reserveWh: config.reserveWh,
    bankWh: startBankWh ?? config.defaultStartBankWh,
    startBankWh: startBankWh ?? config.defaultStartBankWh,
    lastKey: null,
    work: { target: null, taps: 0 },
    combo: ComboSystem.create(),
    rng: {
      barrel: rngFrom(deriveSeed(seed, "stage:barrel")),
      bite: rngFrom(deriveSeed(seed, "stage:bite")),
      crack: rngFrom(deriveSeed(seed, "stage:crack")),
    },
    stats: { jumpsCleared: 0, smashes: 0, built: 0, pried: 0, hits: 0, taps: 0 },
  };
}

// ── helpers ─────────────────────────────────────────────────────────

const tierYSub = (s, tier) => toSub(s.config.tiersY[tier]);
const climbable = (l) => l.rungs >= l.need;
const ev = (s, type, extra = {}) => ({ type, t: s.tick * TICK_MS, ...extra });

function end(s, status, events) {
  return { state: { ...s, status, endedAt: s.tick * TICK_MS }, events: [...events, ev(s, status)] };
}

// ── player ──────────────────────────────────────────────────────────

function stepPlayer(s) {
  const p = { ...s.player };
  const { keys } = s;
  const events = [];
  const tick = s.tick;
  const biting = p.biteUntil > tick;
  const stunned = p.stunUntil > tick;

  if (p.mode === "air") {
    p.jumpTick += 1;
    p.x = Math.min(MAX_X, Math.max(MIN_X, p.x + p.vx));
    if (p.jumpTick >= JUMP_TICKS) {
      p.mode = "ground";
      p.y = tierYSub(s, p.tier);
      events.push(ev(s, "land"));
    } else {
      p.y = tierYSub(s, p.tier) - jumpHeightSub(p.jumpTick);
    }
    return { state: { ...s, player: p }, events };
  }

  if (p.mode === "ladder") {
    const ladder = s.ladders.find((l) => l.id === p.ladderId);
    if (keys.U && !keys.D) p.y -= CLIMB_SUB;
    else if (keys.D && !keys.U) p.y += CLIMB_SUB;
    if (p.y <= tierYSub(s, ladder.to)) {
      Object.assign(p, { mode: "ground", tier: ladder.to, y: tierYSub(s, ladder.to), ladderId: null, entryX: ladder.x });
      events.push(ev(s, "climbed", { tier: ladder.to }));
    } else if (p.y >= tierYSub(s, ladder.from)) {
      Object.assign(p, { mode: "ground", tier: ladder.from, y: tierYSub(s, ladder.from), ladderId: null });
    }
    return { state: { ...s, player: p }, events };
  }

  // Grounded
  if (stunned) return { state: s, events };
  const wantsJump = s.jumpRequestUntil >= tick && !biting;
  if (wantsJump) {
    const vx = keys.R && !keys.L ? WALK_SUB : keys.L && !keys.R ? -WALK_SUB : 0;
    Object.assign(p, { mode: "air", jumpTick: 1, vx });
    p.x = Math.min(MAX_X, Math.max(MIN_X, p.x + vx));
    p.y = tierYSub(s, p.tier) - jumpHeightSub(1);
    events.push(ev(s, "jump"));
    return { state: { ...s, player: p, jumpRequestUntil: -1 }, events };
  }

  if (!biting && keys.U) {
    const up = s.ladders.find((l) => l.from === p.tier && climbable(l) && aligned(p.x, l.x));
    if (up) {
      Object.assign(p, { mode: "ladder", ladderId: up.id, x: up.x, y: p.y - CLIMB_SUB });
      return { state: { ...s, player: p }, events };
    }
  }
  if (!biting && keys.D) {
    const down = s.ladders.find((l) => l.to === p.tier && climbable(l) && aligned(p.x, l.x));
    if (down) {
      Object.assign(p, { mode: "ladder", ladderId: down.id, x: down.x, y: p.y + CLIMB_SUB });
      return { state: { ...s, player: p }, events };
    }
  }

  const dir = keys.R && !keys.L ? 1 : keys.L && !keys.R ? -1 : 0;
  if (dir !== 0) p.x = Math.min(MAX_X, Math.max(MIN_X, p.x + dir * WALK_SUB));

  // Beaver Bite pickup (Donkey Kong hammer)
  let bite = s.bite;
  if (!bite.taken && bite.tier === p.tier && Math.abs(bite.x - p.x) < PICKUP_DX) {
    bite = { ...bite, taken: true };
    p.biteUntil = tick + s.config.biteTicks;
    events.push(ev(s, "bite"));
  }
  return { state: { ...s, player: p, bite }, events };
}

// ── barrels ─────────────────────────────────────────────────────────

function stepBarrel(s, barrel, rng) {
  const b = { ...barrel };
  if (b.mode === "fall") {
    b.fall += 1;
    const from = tierYSub(s, b.tier);
    const to = tierYSub(s, b.tier - 1);
    b.y = from + Math.floor(((to - from) * b.fall) / BARREL_FALL_TICKS);
    if (b.fall >= BARREL_FALL_TICKS) {
      Object.assign(b, { mode: "roll", tier: b.tier - 1, y: to, dir: tierDir(b.tier - 1), fall: 0, lastLadder: null });
    }
    return { barrel: b, rng };
  }
  if (b.mode === "ladder") {
    const ladder = s.ladders.find((l) => l.id === b.ladderId);
    b.y += BARREL_LADDER_SUB;
    if (b.y >= tierYSub(s, ladder.from)) {
      Object.assign(b, { mode: "roll", tier: ladder.from, y: tierYSub(s, ladder.from), dir: tierDir(ladder.from) });
    }
    return { barrel: b, rng };
  }

  const prevX = b.x;
  b.x += b.dir * s.config.barrelSpeedSub;
  // Maybe drop down a ladder it rolls over (Donkey Kong: seeded, ~1 in 4)
  const crossed = s.ladders.find(
    (l) => l.to === b.tier && climbable(l) && l.id !== b.lastLadder && Math.min(prevX, b.x) <= l.x && l.x <= Math.max(prevX, b.x),
  );
  let r = rng;
  if (crossed) {
    const roll = nextInt(r, 100);
    r = roll.rng;
    b.lastLadder = crossed.id;
    if (roll.value < s.config.ladderTakePct) {
      Object.assign(b, { mode: "ladder", ladderId: crossed.id, x: crossed.x });
      return { barrel: b, rng: r };
    }
  }
  if ((b.dir > 0 && b.x >= MAX_X) || (b.dir < 0 && b.x <= MIN_X)) {
    if (b.tier === 0) return { barrel: null, rng: r };
    Object.assign(b, { mode: "fall", fall: 0 });
  }
  return { barrel: b, rng: r };
}

function stepBarrels(s) {
  let rng = s.rng.barrel;
  const barrels = [];
  for (const barrel of s.barrels) {
    const out = stepBarrel(s, barrel, rng);
    rng = out.rng;
    if (out.barrel) barrels.push(out.barrel);
  }
  return { ...s, barrels, rng: { ...s.rng, barrel: rng } };
}

// ── sparks (defend stages): chase the player, climbing toward their tier ──

const SPARK_TURN_PX = 40; // a spark overshoots you by this much before turning

function stepSpark(s, spark) {
  const sp = { ...spark };
  const speed = s.config.sparkSpeedSub;
  const target = s.player;
  if (sp.mode === "ladder") {
    const ladder = s.ladders.find((l) => l.id === sp.ladderId);
    const goingUp = target.tier > sp.tier;
    sp.y += goingUp ? -speed : speed;
    const arriveTier = goingUp ? ladder.to : ladder.from;
    if ((goingUp && sp.y <= tierYSub(s, ladder.to)) || (!goingUp && sp.y >= tierYSub(s, ladder.from))) {
      Object.assign(sp, { mode: "ground", tier: arriveTier, y: tierYSub(s, arriveTier), ladderId: null });
    }
    return sp;
  }
  if (target.tier !== sp.tier) {
    const up = target.tier > sp.tier;
    const options = s.ladders.filter((l) => climbable(l) && (up ? l.from === sp.tier : l.to === sp.tier));
    if (options.length > 0) {
      const ladder = options.reduce((best, l) => (Math.abs(l.x - sp.x) < Math.abs(best.x - sp.x) ? l : best));
      if (Math.abs(ladder.x - sp.x) <= speed) return { ...sp, x: ladder.x, mode: "ladder", ladderId: ladder.id };
      sp.dir = Math.sign(ladder.x - sp.x);
    }
  } else if ((target.x - sp.x) * sp.dir < -toSub(SPARK_TURN_PX)) {
    // Donkey Kong fireballs don't home in: charge past, then turn around
    sp.dir = -sp.dir;
  }
  sp.x += sp.dir * speed;
  if (sp.x <= MIN_X || sp.x >= MAX_X) {
    sp.x = Math.min(MAX_X, Math.max(MIN_X, sp.x));
    sp.dir = -sp.dir;
  }
  return sp;
}

// ── collisions ──────────────────────────────────────────────────────

function heightAboveGirder(s) {
  return s.player.mode === "air" ? jumpHeightSub(s.player.jumpTick) : 0;
}

function overlapsActor(s, actor) {
  const p = s.player;
  if (Math.abs(actor.x - p.x) >= HIT_DX) return false;
  if (p.mode === "ladder" || actor.mode !== "roll") return Math.abs(actor.y - p.y) < HIT_DY;
  return actor.tier === p.tier;
}

function hitPlayer(s, events, cause) {
  const cfg = s.config;
  const stats = { ...s.stats, hits: s.stats.hits + 1 };
  if (s.kind === "defend") {
    const player = { ...s.player, stunUntil: s.tick + cfg.stunTicks, invulnUntil: s.tick + cfg.invulnTicks };
    return {
      state: { ...s, player, stats, bankWh: s.bankWh - cfg.hitPenaltyWh },
      events: [...events, ev(s, "hit", { wh: -cfg.hitPenaltyWh, cause })],
    };
  }
  const lives = s.lives - 1;
  const hitEvents = [...events, ev(s, "hit", { cause }), ev(s, "life", { lives })];
  if (lives <= 0) return end({ ...s, lives, stats }, "failed", hitEvents);
  // Donkey Kong: lose a life, respawn where you entered this tier, barrels cleared
  const p = s.player;
  const tier = p.mode === "ladder" ? s.ladders.find((l) => l.id === p.ladderId).from : p.tier;
  const player = {
    ...p,
    mode: "ground",
    tier,
    x: tier === p.tier ? p.entryX : toSub(cfg.start.x),
    y: tierYSub(s, tier),
    ladderId: null,
    invulnUntil: s.tick + cfg.invulnTicks,
  };
  return { state: { ...s, lives, stats, player, barrels: [] }, events: hitEvents };
}

function resolveCollisions(s) {
  let state = s;
  let events = [];
  const biting = s.player.biteUntil > s.tick;
  const invulnerable = s.player.invulnUntil > s.tick;
  const h = heightAboveGirder(s);
  const barrels = [];
  let rngBite = s.rng.bite;

  for (const barrel of s.barrels) {
    const b = { ...barrel };
    const p = state.player;
    const sameTierRolling = b.mode === "roll" && p.mode !== "ladder" && b.tier === p.tier;
    if (biting && sameTierRolling && Math.abs(b.x - p.x) < BITE_REACH) {
      const pick = nextInt(rngBite, s.config.bitePointsWh.length);
      rngBite = pick.rng;
      const wh = s.config.bitePointsWh[pick.value];
      state = { ...state, bankWh: state.bankWh + wh, stats: { ...state.stats, smashes: state.stats.smashes + 1 } };
      events.push(ev(s, "smash", { wh }));
      continue; // barrel destroyed
    }
    if (overlapsActor(state, b)) {
      if (sameTierRolling && h >= CLEAR_HEIGHT) {
        if (!b.jumped) {
          b.jumped = true;
          state = {
            ...state,
            bankWh: state.bankWh + s.config.jumpBonusWh,
            stats: { ...state.stats, jumpsCleared: state.stats.jumpsCleared + 1 },
          };
          events.push(ev(s, "jumped", { wh: s.config.jumpBonusWh }));
        }
      } else if (!invulnerable) {
        const cause = `barrel-${b.mode}/player-${p.mode}`;
        const out = hitPlayer({ ...state, barrels: [...barrels, b, ...s.barrels.slice(s.barrels.indexOf(barrel) + 1)] }, events, cause);
        return { state: { ...out.state, rng: { ...out.state.rng, bite: rngBite } }, events: out.events };
      }
    }
    barrels.push(b);
  }
  state = { ...state, barrels, rng: { ...state.rng, bite: rngBite } };

  if (!invulnerable) {
    const hit = state.sparks.find(
      (spark) => overlapsActor(state, { ...spark, mode: spark.mode === "ladder" ? "ladder" : "roll" }) && h < CLEAR_HEIGHT,
    );
    if (hit) {
      // The spark that zapped you jumps back to where it started (no stun-lock)
      const origin = state.config.sparks[hit.id];
      const sparks = state.sparks.map((sp) =>
        sp.id === hit.id
          ? { ...sp, x: toSub(origin.x), y: tierYSub(state, origin.tier), tier: origin.tier, mode: "ground", ladderId: null, dir: -sp.dir }
          : sp,
      );
      return hitPlayer({ ...state, sparks }, events, `spark/player-${state.player.mode}`);
    }
  }
  return { state, events };
}

// ── robots and energy ───────────────────────────────────────────────

function stepRobots(s) {
  let state = s;
  const events = [];
  // Leeches latch onto free turbines when their time comes
  const due = state.leechQueue.filter((q) => q.at <= state.tick);
  if (due.length > 0) {
    state = {
      ...state,
      leechQueue: state.leechQueue.filter((q) => q.at > state.tick),
      leeches: [...state.leeches, ...due.map((q) => ({ turbine: q.turbine, since: state.tick }))],
    };
    due.forEach((q) => events.push(ev(s, "leech", { turbine: q.turbine })));
  }
  // Saboteur: crack a random climbable ladder
  if (state.tick >= state.nextCrackTick) {
    const candidates = state.ladders.filter(climbable);
    let rng = state.rng.crack;
    let ladders = state.ladders;
    if (candidates.length > 0) {
      const pick = nextInt(rng, candidates.length);
      rng = pick.rng;
      const id = candidates[pick.value].id;
      ladders = ladders.map((l) => (l.id === id ? { ...l, rungs: l.need - state.config.crackRungs, cracked: true } : l));
      events.push(ev(s, "crack", { ladder: id }));
    }
    state = { ...state, ladders, nextCrackTick: state.tick + state.config.crackEveryTicks, rng: { ...state.rng, crack: rng } };
  }
  return { state, events };
}

function stepEnergy(s) {
  if ((s.tick + 1) % TICKS_PER_SEC !== 0) return s;
  const cfg = s.config;
  const leak =
    s.leeches.length * cfg.leechDrainWhPerSec +
    s.ladders.filter((l) => l.cracked && !climbable(l)).length * cfg.crackLeakWhPerSec;
  return { ...s, reserveWh: Math.max(0, s.reserveWh - cfg.reserveDrainWhPerSec), bankWh: s.bankWh - leak };
}

function spawnBarrels(s) {
  if (s.tick < s.nextBarrelTick) return { state: s, events: [] };
  const cfg = s.config;
  const jitter = nextInt(s.rng.barrel, cfg.barrelJitterTicks * 2 + 1);
  const barrel = {
    id: s.barrelSeq + 1,
    x: toSub(cfg.barrelSpawnX),
    y: tierYSub(s, cfg.boss.tier),
    tier: cfg.boss.tier,
    dir: tierDir(cfg.boss.tier),
    mode: "roll",
    jumped: false,
    lastLadder: null,
    fall: 0,
  };
  return {
    state: {
      ...s,
      barrels: [...s.barrels, barrel],
      barrelSeq: barrel.id,
      nextBarrelTick: s.tick + cfg.barrelEveryTicks + jitter.value - cfg.barrelJitterTicks,
      rng: { ...s.rng, barrel: jitter.rng },
    },
    events: [ev(s, "barrel")],
  };
}

function checkEnd(s, events) {
  if (s.status !== "playing") return { state: s, events };
  if (s.kind === "build") {
    if (s.player.tier === s.config.goalTier && s.player.mode === "ground") return end(s, "complete", events);
    if (s.reserveWh <= 0) return end(s, "failed", events);
  } else {
    if (s.bankWh <= 0) return end({ ...s, bankWh: 0 }, "failed", events);
    if (s.tick + 1 >= s.config.surviveTicks) return end(s, "complete", events);
  }
  return { state: s, events };
}

// ── tick loop ───────────────────────────────────────────────────────

function step(s0) {
  let events = [];
  const collect = (out) => {
    events = events.concat(out.events);
    return out.state;
  };
  let s = collect(stepPlayer(s0));
  s = stepBarrels(s);
  s = { ...s, sparks: s.sparks.map((sp) => stepSpark(s, sp)) };
  s = collect(resolveCollisions(s));
  if (s.status !== "playing") return { state: s, events };
  s = collect(stepRobots(s));
  s = stepEnergy(s);
  s = collect(spawnBarrels(s));
  s = collect(checkEnd(s, []));
  return { state: { ...s, tick: s.tick + 1 }, events };
}

/** Run every tick that has completed by time `t` (ms). */
export function advance(state, t) {
  let s = state;
  let events = [];
  while (s.status === "playing" && (s.tick + 1) * TICK_MS <= t) {
    const out = step(s);
    s = out.state;
    events = events.concat(out.events);
  }
  if (s.status === "playing" && t > s.t) s = { ...s, t };
  return { state: s, events };
}

// ── input ───────────────────────────────────────────────────────────

/** The work spot under the player: a broken/cracked ladder base or a leeched turbine. */
export function workTarget(s) {
  const p = s.player;
  if (p.mode !== "ground" || p.stunUntil > s.tick || s.keys.L || s.keys.R) return null;
  const ladder = s.ladders.find((l) => l.from === p.tier && !climbable(l) && aligned(p.x, l.x));
  if (ladder) return { kind: "ladder", id: ladder.id };
  const leech = s.leeches.find((lc) => {
    const tb = s.turbines.find((x) => x.id === lc.turbine);
    return tb.tier === p.tier && aligned(p.x, tb.x, WORK_DX_PX);
  });
  return leech ? { kind: "leech", id: leech.turbine } : null;
}

function chuck(s, key, t) {
  const target = workTarget(s);
  if (!target) return { state: s, events: [] };
  const sameTarget = s.work.target && s.work.target.kind === target.kind && s.work.target.id === target.id;
  const work = sameTarget ? s.work : { target, taps: 0 };
  const lastKey = sameTarget ? s.lastKey : null;
  const correct = isCorrectTap(lastKey, key, []);
  const combo = ComboSystem.onTap(s.combo, t + COMBO_CLOCK_OFFSET, correct);
  let state = { ...s, combo: combo.state, work, lastKey, stats: { ...s.stats, taps: s.stats.taps + 1 } };
  if (!correct) return { state, events: [ev(state, "wrongKey")] };

  const taps = work.taps + 1;
  state = { ...state, lastKey: key, work: { target, taps } };
  const events = [];
  if (target.kind === "ladder") {
    if (taps % 2 !== 0) return { state, events };
    const ladder = state.ladders.find((l) => l.id === target.id);
    const rungs = ladder.rungs + 1;
    const done = rungs >= ladder.need;
    state = { ...state, ladders: state.ladders.map((l) => (l.id === ladder.id ? { ...l, rungs, cracked: done ? false : l.cracked } : l)) };
    events.push(ev(state, "rung", { ladder: ladder.id, rungs }));
    if (done) {
      if (ladder.cracked) {
        events.push(ev(state, "repaired", { ladder: ladder.id }));
      } else {
        const wh = state.config.turbineBonusWh;
        state = { ...state, bankWh: state.bankWh + wh, stats: { ...state.stats, built: state.stats.built + 1 } };
        events.push(ev(state, "built", { ladder: ladder.id, wh }));
      }
      state = { ...state, work: { target: null, taps: 0 }, lastKey: null };
    }
    return { state, events };
  }

  if (taps >= state.config.pryTaps) {
    const wh = state.config.pryBonusWh;
    state = {
      ...state,
      leeches: state.leeches.filter((l) => l.turbine !== target.id),
      leechQueue: [...state.leechQueue, { turbine: target.id, at: state.tick + state.config.leechRespawnTicks }],
      bankWh: state.bankWh + wh,
      stats: { ...state.stats, pried: state.stats.pried + 1 },
      work: { target: null, taps: 0 },
      lastKey: null,
    };
    events.push(ev(state, "pried", { turbine: target.id, wh }));
  }
  return { state, events };
}

/**
 * The player pressed / released a key at time `t` (ms since stage start).
 * @returns {{ state: object, events: object[] }}
 */
export function input(state, key, t) {
  if (state.status !== "playing") return { state, events: [] };
  const pre = advance(state, t);
  let s = pre.state;
  if (s.status !== "playing") return pre;

  if (HELD[key]) {
    const [name, down] = HELD[key];
    return { state: { ...s, keys: { ...s.keys, [name]: down } }, events: pre.events };
  }
  if (key === "J") {
    return { state: { ...s, jumpRequestUntil: s.tick + JUMP_BUFFER_TICKS }, events: pre.events };
  }
  if (key === "W" || key === "C") {
    const out = chuck(s, key, t);
    return { state: out.state, events: pre.events.concat(out.events) };
  }
  return pre;
}

// ── results ─────────────────────────────────────────────────────────

export function result(s) {
  const totalWh = s.bankWh + (s.kind === "build" && s.status === "complete" ? s.reserveWh : 0);
  const woodLogs = Math.floor(Math.max(0, totalWh) / s.config.whPerLog);
  return {
    status: s.status,
    level: s.level,
    kind: s.kind,
    bankWh: s.bankWh,
    reserveWh: s.reserveWh,
    totalWh,
    woodLogs,
    woodDelta: woodLogs - Math.floor(s.startBankWh / s.config.whPerLog),
    timeMs: s.endedAt ?? s.t,
    lives: s.lives,
    maxCombo: s.combo.maxCombo,
    ...s.stats,
  };
}

/** Replay an input log from scratch. */
export function replay({ inputs, endAt, ...params }) {
  let s = createStageSim(params);
  for (const [t, key] of inputs) {
    s = input(s, key, t).state;
    if (s.status !== "playing") break;
  }
  if (s.status === "playing" && endAt !== undefined) s = advance(s, endAt).state;
  return result(s);
}
